import { FC, useEffect, useRef, useState } from 'react';
import { CircularProgress, LinearProgress } from '@mui/material';
import styled from 'styled-components';
import { useSwitchTransition } from 'transition-hook';
import { Offline, Online } from 'react-detect-offline';

import { metaTitle } from '../../util/tabTitle';
import PresentationSlide from './PresentationSlide';
import { createDatabase, getById } from '../../indexedDb';
import { DEFAULT_TIME_OUT, DEFAULT_TRANS_TIME } from './constants';
import { GetPresentationResponse } from '../../api-http/presentations';
import { GetDisplayPresentationResponse } from '../../api-http/displays';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background: #252525;
  overflow: hidden;
`;

const StyledOverlay = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: white;
  display: flex;
  flex-direction: row-reverse;
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 1;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px;
  border-radius: 50px;
`;

const RedDotWithOverlay = styled.div`
  position: relative;
  width: 10px;
  height: 10px;
  background-color: red;
  border-radius: 50%;
`;

interface PresentationPreviewProps {
  presentation: GetDisplayPresentationResponse | GetPresentationResponse;
  margin?: number;
  isSyncing?: boolean;
}

function getVideo(src: string) {
  const video = document.createElement('video');
  video.preload = 'auto';
  video.src = src;
  video.load();
  return video;
}

const getAssetExtension = (src: string) =>
  src.split('?')[0].split('.').pop()?.toLowerCase() || '';

const waitForImage = async (image: HTMLImageElement) => {
  if (image.complete && image.naturalWidth > 0) {
    return;
  }

  if (image.decode) {
    try {
      await image.decode();
      return;
    } catch {
      // Some older WebViews reject decode() while still firing load normally.
    }
  }

  await new Promise<void>((resolve) => {
    const complete = () => {
      window.clearTimeout(timeoutId);
      image.removeEventListener('load', complete);
      image.removeEventListener('error', complete);
      resolve();
    };
    const timeoutId = window.setTimeout(complete, 5000);
    image.addEventListener('load', complete, { once: true });
    image.addEventListener('error', complete, { once: true });
  });
};

const PresentationPreview: FC<PresentationPreviewProps> = ({
  presentation,
  margin,
  isSyncing,
}) => {
  metaTitle('Preview');

  // The presentation actually being rendered. This is deliberately a
  // separate copy from the `presentation` prop: it, slideIndex, kvsData and
  // assetsList are only ever updated together (in init(), once the new
  // assets are ready), so a render can never mix a new presentation's
  // slides with a stale slideIndex/assetsList from the old one - which was
  // producing an out-of-bounds/missing-asset lookup and a blank flash.
  const [activePresentation, setActivePresentation] = useState(presentation);
  const [slideIndex, setSlideIndex] = useState(0);
  const [kvsData, setKvsData] = useState<any>({});
  const [assetsList, setAssetsList] = useState<any>(null);
  const slideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const init = async () => {
    const indexedDb = await createDatabase();
    const nextAssetsList: any = {};

    await Promise.all(
      (presentation?.assets || []).map(async (asset) => {
        if (!asset?.assetSrc || !asset?.assetId) {
          return;
        }

        const { assetSrc, assetId } = asset;
        const extension = getAssetExtension(assetSrc);
        let assetUrl = assetSrc;

        try {
          const cachedAsset = await getById(indexedDb, assetId, 'assets');
          if (cachedAsset?.blob) {
            assetUrl = URL.createObjectURL(cachedAsset.blob);
          }
        } catch {
          // Fall back to the remote asset URL if IndexedDB is unavailable.
        }

        if (['jpg', 'png', 'jpeg', 'gif'].includes(extension)) {
          const image = new Image();
          image.decoding = 'async';
          image.src = assetUrl;
          await waitForImage(image);
          nextAssetsList[assetId] = image;
        } else if (['mp4', 'webm'].includes(extension)) {
          nextAssetsList[assetId] = getVideo(assetUrl);
        }
      }),
    );

    setAssetsList(nextAssetsList);
    setSlideIndex(0);
    setKvsData(presentation?.kvs || {});
    setActivePresentation(presentation);
  };

  useEffect(() => {
    // Deliberately don't clear assetsList here: doing so would blank the
    // current slide immediately. Instead keep rendering it until init()
    // finishes preparing the new assets, then swap in one go.
    if (presentation) {
      init();
    }
  }, [presentation]);

  useEffect(() => {
    if (slideTimeoutRef.current) {
      clearTimeout(slideTimeoutRef.current);
    }

    const currentSlide = activePresentation?.slides?.[slideIndex];
    const slideCount = activePresentation?.slides?.length || 0;
    if (!assetsList || !currentSlide || slideCount === 0) {
      return;
    }

    slideTimeoutRef.current = setTimeout(() => {
      setSlideIndex((currentIndex) =>
        currentIndex + 1 >= slideCount ? 0 : currentIndex + 1,
      );
    }, (currentSlide.duration || DEFAULT_TIME_OUT) + 2000);

    return () => {
      if (slideTimeoutRef.current) {
        clearTimeout(slideTimeoutRef.current);
      }
    };
  }, [assetsList, activePresentation?.slides, slideIndex]);

  const currentSlide = activePresentation?.slides[slideIndex];
  const transition = useSwitchTransition(
    currentSlide,
    DEFAULT_TRANS_TIME,
    'default',
  );
  const slide = currentSlide?.slideData as any;
  if (!slide || !assetsList) {
    return <LinearProgress />;
  }

  return (
    <Container>
      <Offline>
        <StyledOverlay>
          <RedDotWithOverlay />
        </StyledOverlay>
      </Offline>
      <Online>
        {isSyncing && (
          <StyledOverlay>
            <CircularProgress color="inherit" size={20} />
          </StyledOverlay>
        )}
      </Online>
      {transition((state, stage) => {
        return (
          <PresentationSlide
            currentSlide={state}
            stage={stage}
            kvsData={kvsData}
            assetsList={assetsList}
            margin={margin}
          />
        );
      })}
    </Container>
  );
};

export default PresentationPreview;
